import { DialogContentText, IconButton } from "@material-ui/core";
import ActionDialog from "@saleor/components/ActionDialog";
import { PAGINATE_BY } from "@saleor/config";
import CoverListPage from "@saleor/cover/components/CoverListPage";
import { useCoverBulkDeleteMutation } from "@saleor/cover/mutations";
import { useCoverListQuery } from "@saleor/cover/queries";
import {
  coverAddUrl,
  coverListUrl,
  CoverListUrlDialog,
  CoverListUrlQueryParams,
  coverUrl
} from "@saleor/cover/urls";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
import { getSortParams } from "@saleor/utils/sort";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  areFiltersApplied,
  getFilterTabs,
  getFilterVariables
} from "./filters";
import { getSortQueryVariables } from "./sort";

interface CoverListProps {
  params: CoverListUrlQueryParams;
}

const CoverList: React.FC<CoverListProps> = ({ params }) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const notify = useNotifier();

  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );
  const intl = useIntl();

  const paginationState = createPaginationState(PAGINATE_BY, params);

  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params),
      sort: getSortQueryVariables(params)
    }),
    [params]
  );

  const { data, loading, refetch } = useCoverListQuery({
    variables: queryVariables
  });

  const [coverBulkDelete, coverBulkDeleteOpts] = useCoverBulkDeleteMutation({
    onCompleted: data => {
      if (data.coverBulkDelete.errors.length === 0) {
        closeModal();
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Cover successfully delete",
            description: "deleted multiple Cover"
          })
        });
        reset();
        refetch();
      }
    }
  });

  const tabs = getFilterTabs();

  const getFilterQueryParam = null;

  const [, resetFilters, handleSearchChange] = createFilterHandlers({
    cleanupFn: reset,
    createUrl: coverListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const handleTabChange = (tab: number) => {
    reset();
    navigate(
      coverListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };

  const [openModal, closeModal] = createDialogActionHandlers<
    CoverListUrlDialog,
    CoverListUrlQueryParams
  >(navigate, coverListUrl, params);

  const { updateListSettings, settings } = useListSettings(
    ListViews.COVER_LIST
  );

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.covers.pageInfo),
    paginationState,
    params
  );
  const handleSort = createSortHandler(navigate, coverListUrl, params);

  return (
    <>
      <CoverListPage
        toatlCount = {maybe(() => data.covers.totalCount,0)}
        cover={maybe(() => data.covers.edges.map(edge => edge.node))}
        initialSearch={params.query || ""}
        onSearchChange={handleSearchChange}
        currentTab={currentTab}
        onAdd={() => navigate(coverAddUrl())}
        onAll={resetFilters}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        tabs={tabs.map(tab => tab.name)}
        disabled={loading}
        settings={settings}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onSort={handleSort}
        onUpdateListSettings={updateListSettings}
        pageInfo={pageInfo}
        sort={getSortParams(params)}
        onRowClick={id => () => navigate(coverUrl(id))}
        toolbar={
          <IconButton
            color="primary"
            // onClick={() =>
            //   openModal("remove", {
            //     ids: listElements
            //   })
            // }
          >
            {/* <DeleteIcon /> */}
          </IconButton>
        }
        isChecked={isSelected}
        selected={listElements.length}
        toggle={toggle}
        toggleAll={toggleAll}
      />
      <ActionDialog
        open={params.action === "remove" && maybe(() => params.ids.length > 0)}
        confirmButtonState={coverBulkDeleteOpts.status}
        onClose={closeModal}
        onConfirm={() => coverBulkDelete({ variables: { ids: params.ids } })}
        title={intl.formatMessage({
          defaultMessage: "Delete Cover",
          description: "dialog title"
        })}
        variant="delete"
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to delete this cover?} other{Are you sure you want to delete {displayQuantity} covers?}}"
            description="dialog content"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
    </>
  );
};
export default CoverList;
